<template>
  <v-card width="100%">
    <v-row>
      <v-col v-if="isFetched" cols="12">
        <v-list style="padding: 0 !important">
          <!-- NEW ITEM -->
          <v-list-item class="my-6">
            <v-form ref="new-item-form" style="width: 100%">
              <div class="d-flex" style="gap: 1rem; width: 100%;">
                <span style="width: 100%">
                  <data-input v-bind="inputAttributesMetadata.specie" hide-details v-model="newItemData.specie" />
                </span>
                <span style="max-width: 150px; width: 100%">
                  <data-input v-bind="inputAttributesMetadata.coverage" hide-details v-model="newItemData.coverage" />
                </span>
                <span style="width: 70px; min-width: 70px" class="d-flex justify-end">
                  <base-rounded-button
                    icon="mdi-plus"
                    color="success"
                    :loading="isNewLoading"
                    @click="handleEdit('new')"
                    :disabled="!newItemData.specie || !newItemData.coverage"
                  />
                </span>
              </div>
            </v-form>
          </v-list-item>
          <!-- LIST -->
          <template v-for="(specie, index) in species">
            <v-list-item v-if="specie.itemAction !== 'delete'" :key="index" class="list-element-insert">
              <div class="d-flex py-3 align-center" style="gap: 1rem; width: 100%">
                <template>
                  <data-input
                    v-if="editingId === specie.id"
                    v-bind="inputAttributesMetadata.specie"
                    hide-details
                    style="width: 100%"
                    v-model="editItemData.specie"
                  />
                  <v-tooltip v-else bottom left color="font">
                    <template #activator="{ on }">
                      <span v-on="on" class="text-body-2 text-truncate" style="padding: 0 12px; width: 100%">{{
                        getValueText('specie', specie.specie)
                      }}</span>
                    </template>
                    {{ getValueText('specie', specie.specie) }}
                  </v-tooltip>
                </template>
                <span style="max-width: 150px; width: 100%">
                  <data-input
                    v-if="editingId === specie.id"
                    v-bind="inputAttributesMetadata.coverage"
                    hide-details
                    v-model="editItemData.coverage"
                  />
                  <span v-else class="text-body-2" style="padding: 0 12px">{{ specie.coverage }}</span>
                </span>
                <span class="d-flex justify-end" style="width: 70px; min-width: 70px;">
                  <v-btn
                    v-for="(button, buttonIdx) in getItemButtons(specie)"
                    :key="buttonIdx"
                    icon
                    @click="button.click"
                    :loading="button.loading"
                    :disabled="button.disabled"
                  >
                    <v-icon :color="button.color">{{ button.icon }}</v-icon>
                  </v-btn>
                </span>
              </div>
            </v-list-item>
          </template>
        </v-list>
        <main-dialog
          :v-if="isDeleteDialogLoaded"
          :is-visible.sync="isDeleteDialogVisible"
          :loading="isDeleteDialogLoading"
          :title="$i18n.t('table.rziMonitoring.fito.deleteSpecie')"
          :body="$i18n.t('table.rziMonitoring.fito.deleteSpecieBody')"
          @agree="handleEdit('delete')"
          agree-text="dialog.agreeDelete"
        />
      </v-col>
      <v-col v-else cols="12" class="my-4">
        <main-loader />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import MainDialog from '@/components/MainDialog';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import { utils } from '@/mixins/rziHelpers';
import { get, call } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RziFitoSpecies',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    MainLoader,
    DataInput,
    MainDialog,
  },
  props: {
    currentMetadata: {
      type: Object,
      required: true,
    },
    fitoLayerId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    isFormValid: false,
    isFetched: false,
    isNewLoading: false,
    isEditLoading: false,
    editingId: null,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogLoading: false,
    editItemData: {
      coverage: null,
      specie: null,
    },
    newItemData: {
      coverage: null,
      specie: null,
    },
    inputAttributesMetadata: {
      coverage: null,
      specie: null,
    },
    species: [],
  }),
  computed: {
    dataSources: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
  },
  methods: {
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    getValueText(type, value) {
      const typeItem = (this.inputAttributesMetadata[type]?.items ?? []).find(item => item.value === value)?.text;
      return typeItem ?? '';
    },
    getItemButtons(specie) {
      return this.editingId === specie.id
        ? [
            {
              icon: 'mdi-check',
              color: 'success',
              loading: this.isEditLoading,
              disabled: !this.editItemData.specie || !this.editItemData.coverage,
              click: () => this.handleEdit('edit'),
            },
            {
              icon: 'mdi-close',
              color: 'error',
              click: () => this.toggleEditing(null),
            },
          ]
        : [
            {
              icon: 'mdi-pencil',
              click: () => this.toggleEditing(specie),
            },
            {
              icon: 'mdi-delete',
              click: () => this.openDeleteDialog(specie),
            },
          ];
    },
    getEditPayload(type) {
      const attributes = this.currentMetadata.fitoSpecies.attributes;
      switch (type) {
        case 'new':
          return {
            loading: 'isNewLoading',
            payload: {
              insert: [
                {
                  [attributes.layer]: this.fitoLayerId,
                  [attributes.coverage]: this.newItemData.coverage,
                  [attributes.specie]: this.newItemData.specie,
                },
              ],
            },
          };
        case 'edit': {
          return {
            loading: 'isEditLoading',
            payload: {
              update: [
                {
                  fid: this.editingId,
                  properties: {
                    [attributes.layer]: this.fitoLayerId,
                    [attributes.coverage]: this.editItemData.coverage,
                    [attributes.specie]: this.editItemData.specie,
                  },
                },
              ],
            },
          };
        }
        case 'delete': {
          return {
            loading: 'isDeleteDialogLoading',
            payload: {
              delete: {
                features_ids: [this.deleteId],
              },
            },
          };
        }
      }
    },
    async handleEdit(type) {
      const typePayload = this.getEditPayload(type);
      try {
        this[typePayload.loading] = true;
        await this.editMultipleDataSourceFeatures({
          data_source_name: this.currentMetadata.fitoSpecies.datasource,
          ...typePayload.payload,
        });
        await this.init();
        this.toggleEditing(null);
        if (type === 'new') {
          this.newItemData = {
            coverage: null,
            specie: null,
          };
          this.$refs['new-item-form']?.resetValidation();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this[typePayload.loading] = false;
        this.isDeleteDialogVisible = this.isDeleteDialogLoaded = false;
      }
    },
    toggleEditing(item) {
      this.editingId = item?.id ?? null;
      this.editItemData = {
        coverage: item?.coverage ?? null,
        specie: item?.specie ?? null,
      };
    },
    openDeleteDialog(item) {
      this.deleteId = item?.id;
      this.isDeleteDialogVisible = this.isDeleteDialogLoaded = true;
    },
    async init() {
      try {
        const r = await this.getDataSourceFeatures({
          dataSource: this.currentMetadata.fitoSpecies.datasource,
          filters: {
            $EQUAL: {
              [`${this.currentMetadata.fitoSpecies.datasource}.${this.currentMetadata.fitoSpecies.attributes.layer}`]: {
                value: this.fitoLayerId,
              },
            },
          },
        });
        this.species = r.features
          .map(f => ({
            id: f.id,
            coverage: f.properties[this.currentMetadata.fitoSpecies.attributes.coverage],
            specie: f.properties[this.currentMetadata.fitoSpecies.attributes.specie],
            itemAction: 'update',
          }))
          .sort((a, b) => (b.coverage ?? 0) - (a.coverage ?? 0));
        this.isFetched = true;
      } catch (error) {
        console.error(error);
      }
    },
  },
  mounted() {
    this.isFetched = false;
    this.formSchema = this.getForm(
      null,
      this.dataSources[this.currentMetadata.fitoSpecies.datasource]?.attributes_schema.attributes
    );
    this.inputAttributesMetadata = {
      coverage: this.formSchema.find(a => a.attribute === this.currentMetadata.fitoSpecies.attributes.coverage),
      specie: this.formSchema.find(a => a.attribute === this.currentMetadata.fitoSpecies.attributes.specie),
    };
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.list-element-insert {
  @include border(top, 1px);
}
</style>
